import type { WorksheetSpecV1 } from './spec'
import type { WorksheetQueryOptions } from './worksheetQueryBuilder'
import type { SourceSchema } from './sourceSchema'
import type { CanonicalAuthoringState } from './canonicalAuthoringState'
import {
  resolveWorkbookExecutionBinding,
  type WorkbookExecutionBinding,
} from './workbookExecutionBinding'
import {
  resolveWorksheetRuntimeCapabilities,
  type WorksheetExecutionProfile,
  type WorksheetRuntimeCapabilities,
} from './worksheetRuntimeCapabilities'

export type WorksheetRuntimeFilter = {
  field_name: string
  operator: string
  value: any
  filter_type?: 'context' | 'dimension' | 'measure' | string | null
  is_measure?: boolean
  aggregation?: string | null
  context?: boolean
  kind?: string | null
}

export type NormalizedWorksheetRequest = {
  worksheetSpec: WorksheetSpecV1
  worksheetName: string | null
  filters: WorksheetRuntimeFilter[]
  parameters: Record<string, any>
  explicitParameterOverrides: Record<string, any>
  measureValuesOverride: WorksheetQueryOptions['measureValuesOverride']
  sourceSchema?: SourceSchema | null
}

export type UnifiedWorksheetExecutionRequest = {
  authoringState: CanonicalAuthoringState
  normalizedRequest: NormalizedWorksheetRequest
  binding: WorkbookExecutionBinding
  executionProfile: WorksheetExecutionProfile
  capabilities: WorksheetRuntimeCapabilities
}

const normalizeParamValue = (value: any): any => {
  const s = typeof value === 'string' ? value.trim() : ''
  if (s && s.startsWith('#') && s.endsWith('#') && s.length >= 2) return s.slice(1, -1)
  return value
}

const normalizeFilterKey = (value: any): string =>
  String(value ?? '').trim().toLowerCase().replace(/^:+/, '')

export const normalizeWorksheetName = (spec: WorksheetSpecV1 | null | undefined, overrideName?: string | null): string | null => {
  const direct = String(overrideName ?? '').trim()
  if (direct) return direct
  const specName = String(
    (spec as any)?.source?.tableau?.worksheet_name ??
    (spec as any)?.source?.tableau?.worksheetName ??
    (spec as any)?.name ??
    ''
  ).trim()
  return specName || null
}

export const mergeWorksheetFilters = (
  baseFilters: WorksheetRuntimeFilter[] | null | undefined,
  overrideFilters: WorksheetRuntimeFilter[] | null | undefined,
): WorksheetRuntimeFilter[] => {
  const merged = new Map<string, WorksheetRuntimeFilter>()
  for (const filter of Array.isArray(baseFilters) ? baseFilters : []) {
    const key = normalizeFilterKey((filter as any)?.field_name)
    if (key) merged.set(key, filter)
  }
  for (const filter of Array.isArray(overrideFilters) ? overrideFilters : []) {
    const key = normalizeFilterKey((filter as any)?.field_name)
    if (key) merged.set(key, filter)
  }
  return Array.from(merged.values())
}

export const mergeWorksheetParameters = (
  baseParameters: Record<string, any> | null | undefined,
  overrideParameters: Record<string, any> | null | undefined,
  opts?: { addBracketAliases?: boolean },
): Record<string, any> => {
  const merged: Record<string, any> = {}
  for (const [key, value] of Object.entries(baseParameters || {})) {
    const k = String(key ?? '').trim()
    if (!k) continue
    merged[k] = normalizeParamValue(value)
  }
  for (const [key, value] of Object.entries(overrideParameters || {})) {
    const k = String(key ?? '').trim()
    if (!k) continue
    merged[k] = normalizeParamValue(value)
  }
  if (opts?.addBracketAliases === false) return merged
  for (const [key, value] of Object.entries({ ...merged })) {
    const k = String(key ?? '').trim()
    if (!k) continue
    if (k.startsWith('[') && k.endsWith(']') && k.length >= 3) {
      const inner = k.slice(1, -1).trim()
      if (inner && !Object.prototype.hasOwnProperty.call(merged, inner)) merged[inner] = value
      continue
    }
    const bracketed = `[${k}]`
    if (!Object.prototype.hasOwnProperty.call(merged, bracketed)) merged[bracketed] = value
  }
  return merged
}

export const buildExplicitParameterOverrides = (
  mergedParameters: Record<string, any>,
  baseParameters: Record<string, any> | null | undefined,
  rawOverrides: Record<string, any> | null | undefined,
): Record<string, any> => {
  const out: Record<string, any> = {}
  const normalizeKey = (value: any): string => String(value ?? '').trim().replace(/^\[|\]$/g, '')
  const sameValue = (a: any, b: any): boolean => {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return a === b
    }
  }
  const base = baseParameters || {}
  for (const [key, value] of Object.entries(rawOverrides || {})) {
    const k = String(key ?? '').trim()
    if (!k || value === undefined) continue
    const directBase = Object.prototype.hasOwnProperty.call(base, k) ? (base as any)[k] : undefined
    const normalizedBaseKey = normalizeKey(k)
    const normalizedBase = Object.prototype.hasOwnProperty.call(base, normalizedBaseKey)
      ? (base as any)[normalizedBaseKey]
      : undefined
    const hasBase = directBase !== undefined || normalizedBase !== undefined
    const baseValue = directBase !== undefined ? directBase : normalizedBase
    if (!hasBase || !sameValue(value, baseValue)) {
      out[k] = normalizeParamValue(value)
      continue
    }
    if (!sameValue((mergedParameters as any)[k], baseValue)) {
      out[k] = normalizeParamValue(value)
    }
  }
  return out
}

export const buildWorksheetQueryOptionsFromRequest = (
  request: NormalizedWorksheetRequest,
  extras: Omit<WorksheetQueryOptions, 'filters' | 'parameters' | 'worksheetName' | 'measureValuesOverride'>,
): WorksheetQueryOptions => ({
  ...extras,
  sourceSchema: extras.sourceSchema ?? request.sourceSchema ?? null,
  filters: Array.isArray(request.filters) ? request.filters : [],
  parameters: request.parameters || {},
  worksheetName: request.worksheetName || undefined,
  measureValuesOverride: request.measureValuesOverride ?? null,
})

export const buildWorksheetQueryOptionsFromExecutionRequest = (
  request: UnifiedWorksheetExecutionRequest,
  extras: Omit<WorksheetQueryOptions, 'filters' | 'parameters' | 'worksheetName' | 'measureValuesOverride'>,
): WorksheetQueryOptions => buildWorksheetQueryOptionsFromRequest(request.normalizedRequest, extras)

export const buildNormalizedWorksheetRequest = (input: {
  worksheetSpec: WorksheetSpecV1
  worksheetName?: string | null
  baseFilters?: WorksheetRuntimeFilter[] | null
  overrideFilters?: WorksheetRuntimeFilter[] | null
  baseParameters?: Record<string, any> | null
  overrideParameters?: Record<string, any> | null
  measureValuesOverride?: WorksheetQueryOptions['measureValuesOverride']
  sourceSchema?: SourceSchema | null
}): NormalizedWorksheetRequest => {
  const worksheetName = normalizeWorksheetName(input.worksheetSpec, input.worksheetName)
  const filters = mergeWorksheetFilters(input.baseFilters, input.overrideFilters)
  const parameters = mergeWorksheetParameters(input.baseParameters, input.overrideParameters)
  const explicitParameterOverrides = buildExplicitParameterOverrides(
    parameters,
    input.baseParameters || {},
    input.overrideParameters || {},
  )
  return {
    worksheetSpec: input.worksheetSpec,
    worksheetName,
    filters,
    parameters,
    explicitParameterOverrides,
    measureValuesOverride: input.measureValuesOverride ?? null,
    sourceSchema: input.sourceSchema ?? null,
  }
}

export const buildUnifiedWorksheetExecutionRequest = (input: {
  authoringState: CanonicalAuthoringState
  worksheetSpec: WorksheetSpecV1
  worksheetName?: string | null
  baseFilters?: WorksheetRuntimeFilter[] | null
  overrideFilters?: WorksheetRuntimeFilter[] | null
  baseParameters?: Record<string, any> | null
  overrideParameters?: Record<string, any> | null
  measureValuesOverride?: WorksheetQueryOptions['measureValuesOverride']
  sourceSchema?: SourceSchema | null
  executionProfile: WorksheetExecutionProfile
  capabilities?: Partial<WorksheetRuntimeCapabilities> | null
  workbookIdRaw?: any
  importIdRaw?: any
}): UnifiedWorksheetExecutionRequest => {
  const normalizedRequest = buildNormalizedWorksheetRequest({
    worksheetSpec: input.worksheetSpec,
    worksheetName: input.worksheetName,
    baseFilters: input.baseFilters,
    overrideFilters: input.overrideFilters,
    baseParameters: input.baseParameters,
    overrideParameters: input.overrideParameters,
    measureValuesOverride: input.measureValuesOverride,
    sourceSchema: input.sourceSchema,
  })
  const binding = resolveWorkbookExecutionBinding({
    workbookIdRaw: input.workbookIdRaw ?? input.authoringState.workbookLineage.workbookId,
    importIdRaw: input.importIdRaw ?? input.authoringState.importLineage.executionImportId,
    workbookIrRaw: input.authoringState.workbookIr,
    worksheetSpecRaw: input.worksheetSpec,
  })
  return {
    authoringState: input.authoringState,
    normalizedRequest,
    binding,
    executionProfile: input.executionProfile,
    capabilities: resolveWorksheetRuntimeCapabilities(input.capabilities ?? undefined),
  }
}

const normalizeWorksheetLookupKey = (value: any): string =>
  String(value ?? '').trim().toLowerCase()

const normalizeMeasureKey = (value: any): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^:+/, '')
    .replace(/\s+/g, ' ')

const isMeasureNamesField = (value: any): boolean =>
  normalizeMeasureKey(value) === 'measure names'

const isMeasureValuesField = (value: any): boolean => {
  const key = normalizeMeasureKey(value)
  return key === 'measure values' || key === 'multiple values'
}

const resolveWorksheetFromWorkbookIr = (
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
): any | null => {
  const worksheetName = normalizeWorksheetLookupKey(worksheetNameRaw)
  if (!worksheetName) return null
  const worksheets = Array.isArray((workbookIr as any)?.worksheets) ? (workbookIr as any).worksheets : []
  return worksheets.find((worksheet: any) => {
    const names = [
      (worksheet as any)?.worksheet_name,
      (worksheet as any)?.worksheetName,
      (worksheet as any)?.internal_name,
      (worksheet as any)?.internalName,
      (worksheet as any)?.name,
    ]
    return names.some((candidate) => normalizeWorksheetLookupKey(candidate) === worksheetName)
  }) ?? null
}

const pushUniqueMeasureValue = (
  out: string[],
  seen: Set<string>,
  rawValue: any,
): void => {
  const value = String(rawValue ?? '').trim()
  if (!value) return
  const key = normalizeMeasureKey(value)
  if (!key || seen.has(key) || isMeasureNamesField(value) || isMeasureValuesField(value)) return
  seen.add(key)
  out.push(value)
}

const collectMeasureNamesSelection = (out: string[], seen: Set<string>, source: any): void => {
  if (!source) return
  const rawValues = Array.isArray((source as any)?.value)
    ? (source as any).value
    : Array.isArray((source as any)?.values)
      ? (source as any).values
      : (typeof (source as any)?.value !== 'undefined' ? [(source as any).value] : [])
  for (const rawValue of rawValues) {
    pushUniqueMeasureValue(out, seen, rawValue)
  }
}

const stripQuotedFieldToken = (raw: string): string => {
  let s = String(raw ?? '').trim()
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1)
  }
  return s.trim()
}

const resolveFieldTokenToCaptionInWorkbookIr = (rawToken: string, workbookIr: any): string | null => {
  const token = stripQuotedFieldToken(rawToken)
  if (!token) return null
  const cis = Array.isArray((workbookIr as any)?.field_dictionary?.column_instances)
    ? ((workbookIr as any).field_dictionary.column_instances as any[])
    : []
  const norm = (v: string): string => String(v ?? '').trim().toLowerCase()
  const nt = norm(token)
  for (const ci of cis) {
    const name = String(ci?.name ?? '').trim()
    if (!name) continue
    if (norm(name) === nt) {
      const cap = String(ci?.caption ?? '').trim()
      return cap || null
    }
  }
  for (const ci of cis) {
    const name = String(ci?.name ?? '').trim()
    if (!name) continue
    if (nt.endsWith(norm(name)) || norm(name).endsWith(nt)) {
      const cap = String(ci?.caption ?? '').trim()
      return cap || null
    }
  }
  return null
}

const isWorkbookMeasureNamesFilter = (filter: any): boolean => {
  const fieldName =
    (filter as any)?.field_name ??
    (filter as any)?.field ??
    (filter as any)?.caption ??
    (filter as any)?.name
  if (isMeasureNamesField(fieldName)) return true
  const column = String((filter as any)?.column ?? '').trim().toLowerCase()
  return column.includes('[:measure names]')
}

const collectMeasureNamesIncludedFromWorkbookFilter = (
  out: string[],
  seen: Set<string>,
  filter: any,
  workbookIr: any,
): void => {
  const included = Array.isArray((filter as any)?.groupfilter?.summary?.included)
    ? (((filter as any).groupfilter.summary.included) as any[])
    : []
  for (const raw of included) {
    const caption = resolveFieldTokenToCaptionInWorkbookIr(String(raw ?? ''), workbookIr)
    if (caption) {
      pushUniqueMeasureValue(out, seen, caption)
      continue
    }
    pushUniqueMeasureValue(out, seen, raw)
  }
}

const collectRawMeasureNamesIncludedFromWorkbookFilter = (filter: any): string[] => {
  const included = Array.isArray((filter as any)?.groupfilter?.summary?.included)
    ? (((filter as any).groupfilter.summary.included) as any[])
    : []
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of included) {
    pushUniqueMeasureValue(out, seen, stripQuotedFieldToken(String(raw ?? '')))
  }
  return out
}

const looksLikeTableauMeasureMemberToken = (raw: any): boolean => {
  const value = stripQuotedFieldToken(String(raw ?? ''))
  if (!value) return false
  const token = cleanDatasourceQualifiedMemberToken(value)
  return /(^|:)(sum|avg|min|max|count|countd|ctd|usr|pcdf|pcto|attr):/i.test(token) && /:qk(?::\d+)?\]?$/i.test(token)
}

const cleanDatasourceQualifiedMemberToken = (raw: string): string => {
  const value = String(raw ?? '').trim()
  const m = value.match(/\[[^\]]+\]\s*\.\s*\[([^\]]+)\]\s*$/)
  if (m) return String(m[1] ?? '').trim()
  const parts = Array.from(value.matchAll(/\[([^\]]+)\]/g)).map((match) => String(match[1] ?? '').trim()).filter(Boolean)
  return parts.length ? (parts[parts.length - 1] || value).trim() : value
}

const multisetSignatureFromMeasureLabels = (labels: string[]): string => {
  const counts = new Map<string, number>()
  for (const raw of labels) {
    const key = normalizeMeasureKey(raw)
    if (!key) continue
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, n]) => `${k}::${n}`)
    .join('|')
}

export const normalizeMeasureValuesOverrideList = (
  raw: WorksheetQueryOptions['measureValuesOverride'],
): string[] => {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const x of raw) {
    const name =
      typeof x === 'string' ? x : String((x as any)?.field_name ?? (x as any)?.field ?? '').trim()
    if (name) out.push(name)
  }
  return out
}

export const measureValuesOverrideMultisetsEqual = (a: string[] | null, b: string[] | null): boolean => {
  if (!a || !b || a.length === 0 || b.length === 0) return false
  return multisetSignatureFromMeasureLabels(a) === multisetSignatureFromMeasureLabels(b)
}

export const deriveWorksheetMeasureValues = (
  spec: WorksheetSpecV1 | null | undefined,
  workbookIr?: any,
  worksheetNameRaw?: string | null,
): string[] | null => {
  try {
    const worksheet = spec as any
    if (!worksheet || String(worksheet.kind ?? '').trim().toLowerCase() !== 'crosstab') return null
    const out: string[] = []
    const seen = new Set<string>()
    const sheetSpec = (worksheet as any)?.spec ?? {}
    const pushMeasureEntry = (entry: any) => {
      const fieldName = String((entry as any)?.field_name ?? (entry as any)?.field ?? '').trim()
      pushUniqueMeasureValue(out, seen, fieldName)
    }

    const explicitMeasureValues = Array.isArray((sheetSpec as any)?.measureValues)
      ? (sheetSpec as any).measureValues
      : Array.isArray((sheetSpec as any)?.measure_values)
        ? (sheetSpec as any).measure_values
        : []
    for (const entry of explicitMeasureValues) pushMeasureEntry(entry)
    if (out.length > 0) {
      const worksheetName = normalizeWorksheetName(spec, worksheetNameRaw)
      const workbookWorksheet = resolveWorksheetFromWorkbookIr(workbookIr, worksheetName)
      if (workbookWorksheet) {
        const rawIncluded: string[] = []
        const rawSeen = new Set<string>()
        const workbookFilters = Array.isArray((workbookWorksheet as any)?.filters) ? (workbookWorksheet as any).filters : []
        for (const filter of workbookFilters) {
          if (!isWorkbookMeasureNamesFilter(filter)) continue
          for (const raw of collectRawMeasureNamesIncludedFromWorkbookFilter(filter)) {
            pushUniqueMeasureValue(rawIncluded, rawSeen, raw)
          }
        }
        const explicitLooksLabelOnly = out.every((value) => !looksLikeTableauMeasureMemberToken(value))
        const rawLooksAuthoritative = rawIncluded.length === out.length && rawIncluded.every(looksLikeTableauMeasureMemberToken)
        if (explicitLooksLabelOnly && rawLooksAuthoritative) return rawIncluded
      }
      return out
    }

    const columns = Array.isArray((sheetSpec as any)?.columns) ? (sheetSpec as any).columns : []
    for (const column of columns) {
      if (!isMeasureNamesField((column as any)?.field_name ?? (column as any)?.field ?? '')) continue
      const customOrder = Array.isArray((column as any)?.sort?.custom_order)
        ? (column as any).sort.custom_order
        : Array.isArray((column as any)?.sort?.customOrder)
          ? (column as any).sort.customOrder
          : []
      for (const value of customOrder) pushUniqueMeasureValue(out, seen, value)
    }

    const filters = Array.isArray((sheetSpec as any)?.filters) ? (sheetSpec as any).filters : []
    for (const filter of filters) {
      const fieldName = (filter as any)?.field_name ?? (filter as any)?.field ?? (filter as any)?.name
      if (!isMeasureNamesField(fieldName)) continue
      collectMeasureNamesSelection(out, seen, filter)
    }

    const worksheetName = normalizeWorksheetName(spec, worksheetNameRaw)
    const workbookWorksheet = resolveWorksheetFromWorkbookIr(workbookIr, worksheetName)
    if (workbookWorksheet) {
      const workbookFilters = Array.isArray((workbookWorksheet as any)?.filters) ? (workbookWorksheet as any).filters : []
      for (const filter of workbookFilters) {
        if (!isWorkbookMeasureNamesFilter(filter)) continue
        collectMeasureNamesSelection(out, seen, filter)
        collectMeasureNamesIncludedFromWorkbookFilter(out, seen, filter, workbookIr)
      }
    }

    return out.length ? out : null
  } catch {
    return null
  }
}

export const worksheetUsesMeasureNames = (
  spec: WorksheetSpecV1 | null | undefined,
  workbookIr?: any,
  worksheetNameRaw?: string | null,
  measureValuesOverride?: WorksheetQueryOptions['measureValuesOverride'],
): boolean => {
  try {
    const worksheet = spec as any
    if (!worksheet || String(worksheet.kind ?? '').trim().toLowerCase() !== 'crosstab') return false
    const shelves = [
      ...(Array.isArray(worksheet?.spec?.rows) ? worksheet.spec.rows : []),
      ...(Array.isArray(worksheet?.spec?.columns) ? worksheet.spec.columns : []),
      ...(Array.isArray(worksheet?.spec?.filters) ? worksheet.spec.filters : []),
    ]
    const names = shelves
      .map((field: any) => (field as any)?.field_name ?? (field as any)?.field ?? (field as any)?.name ?? '')
      .filter(Boolean)
    if (names.some((name) => isMeasureNamesField(name) || isMeasureValuesField(name))) {
      return true
    }
    const workbookWorksheet = resolveWorksheetFromWorkbookIr(
      workbookIr,
      normalizeWorksheetName(spec, worksheetNameRaw),
    )
    if (workbookWorksheet) {
      const joined = [
        String((workbookWorksheet as any)?.rows?.expr ?? ''),
        String((workbookWorksheet as any)?.cols?.expr ?? ''),
        JSON.stringify((workbookWorksheet as any)?.filters ?? []),
      ]
        .join(' ')
        .toLowerCase()
      if (joined.includes('measure names') || joined.includes('measure values') || joined.includes('multiple values')) {
        return true
      }
    }
    return Array.isArray(measureValuesOverride) && measureValuesOverride.length > 1
  } catch {
    return false
  }
}

/**
 * Collect the table-calc calc-field references in `workbookIr` that
 * drive `detectWorksheetHasTableCalcs` to `true` for `worksheetNameRaw`.
 *
 * This is the same scan as `detectWorksheetHasTableCalcs` but returns
 * the *set* of triggering calc-field captions (or internal field names
 * when no caption is set) instead of a boolean. It is the structured
 * input for the typed-spec lowering that will replace the current
 * `table_calcs_not_yet_typed` gate — the next compiler-rewrite slice
 * needs to know which specific calc fields to parse and emit as
 * `TypedWindowMeasure` entries, not merely that *some* exist.
 *
 * Returns an empty array if the worksheet has no table calcs (the
 * detection path used to rely on `!== 0` vs. `=== 0`; this function
 * lets callers show actionable rejection reasons in diagnostics
 * without rewriting the underlying IR walk).
 */
/**
 * Structured per-field record used by both the label-only collector
 * (`collectWorksheetTableCalcFieldTokens`) and the richer resolver
 * (`resolveWorksheetTableCalcFields`) used by PR7 table-calc lowering.
 */
export type WorksheetTableCalcFieldRecord = {
  /** Display label (caption preferred, else internal name). */
  label: string
  /** Physical field name (`col.name`), used for formula resolution. */
  fieldName: string
  /** Raw Tableau calc formula. Empty string when the trigger is a
   *  persisted `table_calcs` entry without an inline formula. */
  formula: string
  /** Column-instance token that carried persisted table-calc metadata. */
  columnInstanceName?: string
  /** Base field/column referenced by the column instance. */
  columnName?: string
  /** Raw persisted Tableau table-calc metadata from the column instance or column. */
  tableCalcs?: any[]
  /** Optional re-alias used when this table calc is also referenced
   *  by a `crosstab.measureValues` entry. The consumer sets this so
   *  the emitted window / derived measure projects with the MNV
   *  caption instead of the record's default `fieldName`. */
  __mnvAlias?: string
}

/**
 * Rich variant of `collectWorksheetTableCalcFieldTokens` that returns
 * the triggering records WITH their formulas, so the authoring-layer
 * lowering (`src/tableau/buildTypedSpecFromAuthoring.ts`) can classify
 * each formula against the grammar in `tableauCalcFormula.ts` and
 * decide whether to lower it into a `TypedWindowMeasure` or reject
 * with a narrow reason.
 */
export const resolveWorksheetTableCalcFields = (
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts?: {
    cleanBracketedFieldRef?: (value: string) => string | null
    normalizeFieldKey?: (value: string) => string
    worksheetSpecRaw?: any
  },
): WorksheetTableCalcFieldRecord[] => {
  return collectWorksheetTableCalcFieldTokensImpl(workbookIr, worksheetNameRaw, opts, 'records')
}

export const collectWorksheetTableCalcFieldTokens = (
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts?: {
    cleanBracketedFieldRef?: (value: string) => string | null
    normalizeFieldKey?: (value: string) => string
    worksheetSpecRaw?: any
  },
): string[] => {
  return collectWorksheetTableCalcFieldTokensImpl(workbookIr, worksheetNameRaw, opts, 'labels')
}

function collectWorksheetTableCalcFieldTokensImpl(
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts: {
    cleanBracketedFieldRef?: (value: string) => string | null
    normalizeFieldKey?: (value: string) => string
    worksheetSpecRaw?: any
  } | undefined,
  mode: 'labels',
): string[]
function collectWorksheetTableCalcFieldTokensImpl(
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts: {
    cleanBracketedFieldRef?: (value: string) => string | null
    normalizeFieldKey?: (value: string) => string
    worksheetSpecRaw?: any
  } | undefined,
  mode: 'records',
): WorksheetTableCalcFieldRecord[]
function collectWorksheetTableCalcFieldTokensImpl(
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts: {
    cleanBracketedFieldRef?: (value: string) => string | null
    normalizeFieldKey?: (value: string) => string
    worksheetSpecRaw?: any
  } | undefined,
  mode: 'labels' | 'records',
): any {
  const records: WorksheetTableCalcFieldRecord[] = []
  const triggered: string[] = []
  try {
    const worksheetName = String(worksheetNameRaw ?? '').trim()
    if (!worksheetName) return triggered
    const ws = (Array.isArray((workbookIr as any)?.worksheets) ? (workbookIr as any).worksheets : []).find((worksheet: any) => {
      const primary = String(worksheet?.worksheet_name ?? worksheet?.name ?? '').trim()
      const internal = String(worksheet?.internal_name ?? '').trim()
      return primary === worksheetName || internal === worksheetName
    })
    const worksheetSpecEntry = (Array.isArray((workbookIr as any)?.worksheet_specs) ? (workbookIr as any).worksheet_specs : []).find((entry: any) => {
      const primary = String(entry?.worksheet_name ?? entry?.name ?? '').trim()
      const internal = String(entry?.internal_name ?? '').trim()
      return primary === worksheetName || internal === worksheetName
    })
    const qkTokens = (() => {
      const tokens = new Set<string>()
      const addTokensFromText = (raw: any) => {
        const text = String(raw ?? '').trim()
        if (!text) return
        for (const match of text.matchAll(/\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?/g)) {
          const token = String(match?.[2] ?? match?.[1] ?? '').trim()
          if (token && /(^|:)qk(\b|:)/i.test(token)) tokens.add(token)
        }
      }
      const visit = (value: any) => {
        if (!value) return
        if (typeof value === 'string') {
          addTokensFromText(value)
          return
        }
        if (Array.isArray(value)) {
          for (const item of value) visit(item)
          return
        }
        if (typeof value === 'object') {
          for (const nested of Object.values(value)) visit(nested)
        }
      }
      addTokensFromText(`${String(ws?.rows?.expr ?? '')} ${String(ws?.cols?.expr ?? '')}`)
      const visitProjectionSpec = (raw: any): void => {
        if (!raw || typeof raw !== 'object') return
        const specRoot = raw?.worksheet_spec && typeof raw.worksheet_spec === 'object'
          ? raw.worksheet_spec
          : raw
        const body = specRoot?.spec && typeof specRoot.spec === 'object' ? specRoot.spec : specRoot
        const chunks = [
          specRoot?.rows,
          specRoot?.cols,
          specRoot?.columns,
          specRoot?.measureValues,
          specRoot?.measure_values,
          specRoot?.columnGroups,
          body?.rows,
          body?.cols,
          body?.columns,
          body?.measureValues,
          body?.measure_values,
          body?.columnGroups,
          body?.shelves,
          body?.filters,
          body?.marks,
        ]
        for (const chunk of chunks) visit(chunk)
        const layers = Array.isArray(body?.layers) ? body.layers : []
        for (const layer of layers) {
          if (!layer || typeof layer !== 'object') continue
          visit((layer as any).shelves)
          visit((layer as any).filters)
          visit((layer as any).marks)
        }
      }
      visitProjectionSpec(worksheetSpecEntry)
      visitProjectionSpec(opts?.worksheetSpecRaw)
      return Array.from(tokens)
    })()
    const fd = (workbookIr as any)?.field_dictionary
    const fdCols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const fdCis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []
    const normalizeToken = (raw: any): string => {
      let token = String(raw ?? '').trim()
      if (!token) return ''
      token = token.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
      if (!token) return ''
      token = token.replaceAll('\\%', '%').replace(/\\"/g, '"').trim()
      token = token.replace(/:([0-9]+)\]$/i, ']')
      return token
    }
    const stripDatasourceQualifier = (token: string): string => {
      const match = String(token ?? '').trim().match(/^\[[^\]]+\]\.\[(.+)\]$/)
      return match && match[1] ? `[${match[1]}]` : String(token ?? '').trim()
    }
    const ciByName = new Map<string, any>()
    const addCiKey = (raw: any, ci: any) => {
      const key = String(raw ?? '').trim()
      if (key && !ciByName.has(key)) ciByName.set(key, ci)
    }
    for (const ci of fdCis) {
      const name = String(ci?.name ?? '').trim()
      if (!name) continue
      const token = normalizeToken(name)
      const shortToken = stripDatasourceQualifier(token)
      addCiKey(name, ci)
      addCiKey(token, ci)
      addCiKey(shortToken, ci)
      if (token && !token.startsWith('[')) addCiKey(`[${token}]`, ci)
      if (shortToken && !shortToken.startsWith('[')) addCiKey(`[${shortToken}]`, ci)
    }
    const colByName = new Map<string, any>()
    const colByCaption = new Map<string, any>()
    for (const col of fdCols) {
      const name = String(col?.name ?? '').trim()
      if (name && !colByName.has(name)) colByName.set(name, col)
      const caption = String(col?.caption ?? col?.attrs?.caption ?? '').trim()
      if (caption) {
        if (!colByCaption.has(caption)) colByCaption.set(caption, col)
        const bracketedCaption = caption.startsWith('[') ? caption : `[${caption}]`
        if (!colByCaption.has(bracketedCaption)) colByCaption.set(bracketedCaption, col)
      }
    }
    const isTableCalcFormula = (formulaRaw: string): boolean => {
      const formula = String(formulaRaw || '').toUpperCase()
      if (!formula) return false
      return (
        formula.includes('LOOKUP(') ||
        formula.includes('INDEX(') ||
        formula.includes('RUNNING_SUM(') ||
        formula.includes('RUNNING_AVG(') ||
        formula.includes('WINDOW_SUM(') ||
        formula.includes('WINDOW_AVG(') ||
        formula.includes('WINDOW_MIN(') ||
        formula.includes('WINDOW_MAX(') ||
        formula.includes('PREVIOUS_VALUE(') ||
        formula.includes('FIRST(') ||
        formula.includes('LAST(')
      )
    }
    const seen = new Set<string>()
    const recordTriggered = (rawToken: any, col: any, ci: any): void => {
      const caption = String(col?.caption ?? '').trim()
      const colName = String(col?.name ?? '').trim()
      const ciName = String(ci?.name ?? '').trim()
      const label = caption || colName || ciName || String(rawToken ?? '').trim()
      if (!label || seen.has(label)) return
      seen.add(label)
      const formula = String(col?.calculation?.formula ?? '').trim()
      const tableCalcs = [
        ...(Array.isArray(ci?.table_calcs) ? ci.table_calcs : []),
        ...(Array.isArray(col?.calculation?.table_calcs) ? col.calculation.table_calcs : []),
      ]
      const persistedTableCalcOnly = !formula && tableCalcs.length > 0
      triggered.push(label)
      records.push({
        label,
        fieldName: persistedTableCalcOnly ? (ciName || colName || label) : (colName || ciName || label),
        formula,
        columnInstanceName: ciName || undefined,
        columnName: colName || String(ci?.column ?? '').trim() || undefined,
        tableCalcs,
      })
    }
    const checkTokenForTableCalc = (rawToken: any): void => {
      const token = normalizeToken(rawToken)
      if (!token) return
      const shortToken = stripDatasourceQualifier(token)
      const ci =
        ciByName.get(token) ||
        ciByName.get(shortToken) ||
        (token && !token.startsWith('[') ? ciByName.get(`[${token}]`) : null) ||
        (shortToken && !shortToken.startsWith('[') ? ciByName.get(`[${shortToken}]`) : null) ||
        null
      const colId = String(ci?.column ?? '').trim()
      const col =
        (colId ? (colByName.get(colId) || null) : null) ||
        colByCaption.get(token) ||
        colByCaption.get(shortToken) ||
        (() => {
          const clean = opts?.cleanBracketedFieldRef ? opts.cleanBracketedFieldRef(shortToken || token) : null
          if (!clean) return null
          const bracketed = clean.startsWith('[') ? clean : `[${clean}]`
          return colByName.get(bracketed) || colByCaption.get(clean) || colByCaption.get(bracketed) || null
        })()
      const ciCalcs = Array.isArray(ci?.table_calcs) ? ci.table_calcs : []
      if (ciCalcs.length > 0) {
        recordTriggered(rawToken, col, ci)
        return
      }
      const colCalcs = Array.isArray(col?.calculation?.table_calcs) ? col.calculation.table_calcs : []
      if (colCalcs.length > 0) {
        recordTriggered(rawToken, col, ci)
        return
      }
      const formula = String(col?.calculation?.formula ?? '').trim()
      if (formula && isTableCalcFormula(formula)) {
        recordTriggered(rawToken, col, ci)
      }
    }
    for (const token of qkTokens) checkTokenForTableCalc(token)
    const directTokens = new Set<string>()
    const addDirectToken = (raw: any) => {
      const token = String(raw ?? '').trim()
      if (token) directTokens.add(token)
    }
    const collectDirectTokens = (value: any, parentKey: string | null = null): void => {
      if (value == null) return
      if (typeof value === 'string') {
        if (
          parentKey === 'field_name' ||
          parentKey === 'field' ||
          parentKey === 'column' ||
          parentKey === 'measureValues' ||
          parentKey === 'custom_order' ||
          parentKey === 'included'
        ) {
          addDirectToken(value)
        }
        return
      }
      if (Array.isArray(value)) {
        for (const item of value) collectDirectTokens(item, parentKey)
        return
      }
      if (typeof value === 'object') {
        for (const [key, nested] of Object.entries(value)) {
          if (
            key === 'field_name' ||
            key === 'field' ||
            key === 'column' ||
            key === 'measureValues' ||
            key === 'custom_order' ||
            key === 'included'
          ) {
            collectDirectTokens(nested, key)
          } else {
            collectDirectTokens(nested, null)
          }
        }
      }
    }
    collectDirectTokens(ws)
    const collectProjectionDirectTokens = (raw: any): void => {
      if (!raw || typeof raw !== 'object') return
      const specRoot = raw?.worksheet_spec && typeof raw.worksheet_spec === 'object'
        ? raw.worksheet_spec
        : raw
      const body = specRoot?.spec && typeof specRoot.spec === 'object' ? specRoot.spec : specRoot
      const chunks = [
        specRoot?.rows,
        specRoot?.cols,
        specRoot?.columns,
        specRoot?.measureValues,
        specRoot?.measure_values,
        specRoot?.columnGroups,
        body?.rows,
        body?.cols,
        body?.columns,
        body?.measureValues,
        body?.measure_values,
        body?.columnGroups,
        body?.shelves,
        body?.filters,
        body?.marks,
      ]
      for (const chunk of chunks) collectDirectTokens(chunk)
      const layers = Array.isArray(body?.layers) ? body.layers : []
      for (const layer of layers) {
        if (!layer || typeof layer !== 'object') continue
        collectDirectTokens((layer as any).shelves)
        collectDirectTokens((layer as any).filters)
        collectDirectTokens((layer as any).marks)
      }
    }
    collectProjectionDirectTokens(worksheetSpecEntry)
    collectProjectionDirectTokens(opts?.worksheetSpecRaw)
    for (const token of directTokens) checkTokenForTableCalc(token)
    const filterSources: any[] = [
      ...(Array.isArray((ws as any)?.filters) ? ((ws as any).filters as any[]) : []),
      ...(Array.isArray((worksheetSpecEntry as any)?.filters) ? ((worksheetSpecEntry as any).filters as any[]) : []),
      ...(Array.isArray((worksheetSpecEntry as any)?.worksheet_spec?.filters)
        ? (((worksheetSpecEntry as any).worksheet_spec.filters) as any[])
        : []),
      ...(Array.isArray((opts?.worksheetSpecRaw as any)?.filters)
        ? (((opts?.worksheetSpecRaw as any).filters) as any[])
        : []),
      ...(Array.isArray((opts?.worksheetSpecRaw as any)?.spec?.filters)
        ? (((opts?.worksheetSpecRaw as any).spec.filters) as any[])
        : []),
    ]
    for (const filter of filterSources) {
      const column = String((filter as any)?.column ?? '').trim().toLowerCase()
      const field = String((filter as any)?.field_name ?? (filter as any)?.field ?? '').trim()
      const isMeasureNamesFilter =
        column.includes('[:measure names]') ||
        (opts?.normalizeFieldKey ? opts.normalizeFieldKey(field) === 'measure names' : field.trim().toLowerCase() === 'measure names')
      if (!isMeasureNamesFilter) continue
      const included: any[] = Array.isArray((filter as any)?.groupfilter?.summary?.included)
        ? (((filter as any).groupfilter.summary.included) as any[])
        : []
      for (const token of included) checkTokenForTableCalc(token)
    }
    return mode === 'labels' ? triggered : records
  } catch {
    return mode === 'labels' ? triggered : records
  }
}

/**
 * Boolean convenience wrapper around
 * `collectWorksheetTableCalcFieldTokens`. Preserved for existing
 * callers that only need a go/no-go signal.
 */
export const detectWorksheetHasTableCalcs = (
  workbookIr: any,
  worksheetNameRaw: string | null | undefined,
  opts?: { cleanBracketedFieldRef?: (value: string) => string | null; normalizeFieldKey?: (value: string) => string },
): boolean => collectWorksheetTableCalcFieldTokens(workbookIr, worksheetNameRaw, opts).length > 0
